import queryString from 'query-string'
import Axios from 'axios'
import configs from 'configs'
import { localStore } from 'utils/store'

const apiClient = ({ prefix = 'api' }) => {
  let _instance
  let _refreshPromise = null

  const refreshToken = () => {
    if (!_refreshPromise) {
      const token = localStore.get(configs.TOKEN_KEY)
      _refreshPromise = Axios.post(`${configs.API_URL}/${prefix}/auth/refresh`, {}, {
        headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
      })
        .then(res => {
          const newToken = res.data.data.access_token
          localStore.set(configs.TOKEN_KEY, newToken)
          return newToken
        })
        .catch(() => null)
        .finally(() => {
          _refreshPromise = null
        })
    }
    return _refreshPromise
  }

  const get = (requestUrl, payload = {}, params = {}, headers = {}) =>
    request({ url: requestUrl, method: 'get', body: payload, params, headers })

  const put = (requestUrl, payload = {}, headers = {}) =>
    request({ url: requestUrl, method: 'put', body: payload, headers })

  const patch = (requestUrl, payload = {}, headers = {}) =>
    request({ url: requestUrl, method: 'patch', body: payload, headers })

  const post = (requestUrl, payload = {}, isFormDate, headers = {}) =>
    request({ url: requestUrl, method: 'post', body: payload, headers }, isFormDate)

  const _delete = (requestUrl, headers = {}) =>
    request({ url: requestUrl, method: 'delete', headers })

  const request = ({ url, method, params = {}, body, headers = {} }) => {
    const urlWithQuery = `${url}?${queryString.stringify(params)}`
    return axios()({ method, url: urlWithQuery, data: body, headers })
  }

  const axios = () => {
    if (!_instance) {
      _instance = Axios.create({
        baseURL: `${configs.API_URL}/${prefix}`,
        headers: { Accept: 'application/json' },
      })

      _instance.interceptors.request.use(config => {
        const token = localStore.get(configs.TOKEN_KEY)
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      })

      _instance.interceptors.response.use(
        res => res,
        async error => {
          const original = error.config
          const isAuthCall = original.url?.includes('auth/')

          if (error.response?.status !== 401 || original._retried || isAuthCall) {
            throw error
          }
          original._retried = true

          const newToken = await refreshToken()
          if (!newToken) {
            localStore.remove(configs.TOKEN_KEY)
            window.location.href = '/login'
            throw error
          }

          original.headers.Authorization = `Bearer ${newToken}`
          return _instance(original)
        }
      )
    }
    return _instance
  }

  const rawRequest = ({ url, method, params = {}, body, headers = {} }) => {
    const urlWithQuery = `${url}?${queryString.stringify(params)}`
    return Axios({
      method,
      url: `${configs.API_URL}/${prefix}/${urlWithQuery}`,
      data: body,
      headers: { Accept: 'application/json', ...headers },
    })
  }

  const getFormData = (entity = {}, isUpdate = false) => {
    const formData = new FormData()
    if (isUpdate) formData.append('_method', 'PUT')
    for (let key in entity) {
      const value = entity[key]
      if (Array.isArray(value)) {
        value.forEach(item => formData.append(key + '[]', item))
      } else if (value instanceof File) {
        formData.append(key, value)
      } else {
        formData.append(key, value)
      }
    }
    return formData
  }

  return { get, put, patch, post, delete: _delete, axios, rawRequest, getFormData }
}

export const externalApiClient = () => ({
  rawRequest({ url, method, params = {}, body, headers = {} }) {
    const urlWithQuery = `${url}?${queryString.stringify(params)}`
    return Axios({
      method,
      url: urlWithQuery,
      data: body,
      headers: { Accept: 'application/json', ...headers },
    })
  },
})

export default apiClient